import React from 'react';
import { FLOW_QUERY_KEYS } from 'utils/constants/flow.constant';
import { axiosInstance } from 'utils/http';
import { flow } from 'utils/urlConstants';
import { showAlert } from 'redux/actions/AlertActions';
import { useDispatch, useSelector } from 'react-redux';
import { useMutation, useQuery } from 'react-query';

/**
 * Merges export jobs with their corresponding job runs
 * @param {Object} exportJobsResponse - Response from export jobs API
 * @param {Object} jobRunsResponse - Response from job runs API
 * @returns {Object} Combined data with jobRun property added to each job
 */
const mergeExportJobsWithJobRuns = (exportJobsResponse, jobRunsResponse) => {
  if (!exportJobsResponse?.jobs || !jobRunsResponse?.jobRuns) {
    return exportJobsResponse || { jobs: [], count: 0 };
  }

  // Create a map of jobRuns by jobID for efficient lookup
  const jobRunsMap = jobRunsResponse.jobRuns.reduce((acc, jobRun) => {
    acc[jobRun.jobID] = jobRun;
    return acc;
  }, {});

  // Merge job runs into jobs
  const jobsWithRuns = exportJobsResponse.jobs.map(job => ({
    ...job,
    jobRun: jobRunsMap[job.ID] || null,
  }));

  return {
    ...exportJobsResponse,
    jobs: jobsWithRuns,
  };
};


const useCreateFlowNodeExportJob = () => {
  const dispatch = useDispatch();
  const orgId = useSelector(state => state.session.user.primaryOrganization.organization.ID);

  return useMutation(
    async ({
      cleanRoomId, flowId, nodeId, partnerAccountIDs, exportParameters,
    }) => {
      const response = await axiosInstance.post(
        flow(orgId).createFlowNodeExportJobs(cleanRoomId, flowId, nodeId),
        {
          organizationID: orgId,
          cleanRoomID: cleanRoomId,
          flowID: flowId,
          nodeID: nodeId,
          partnerAccountIDs,
          exportParameters,
        },
      );
      return response.data;
    },
    {
      onSuccess: (data) => {
        dispatch(showAlert({
          message: `Successfully created ${data.jobs?.length || 0} export job(s)`,
          type: 'success',
        }));
      },
      onError: (error) => {
        const message = error.response?.data?.message || 'Failed to create export jobs';
        dispatch(showAlert({
          message,
          type: 'error',
        }));
      },
    },
  );
};



const useGetFlowNodeExportJobs = ({
  crId, flowId, nodeId, enabled = true,
}) => {
  const orgId = useSelector(state => state.session.user.primaryOrganization.organization.ID);

  return useQuery(
    [FLOW_QUERY_KEYS.FLOW_NODE_EXPORT_JOBS, crId, flowId, nodeId],
    async () => {
      if (!crId || !flowId || !nodeId) return { jobs: [], count: 0 };

      const response = await axiosInstance.get(
        flow(orgId).getFlowNodeExportJobs(crId, flowId, nodeId),
      );
      return response.data;
    },
    {
      enabled: enabled && crId != null && flowId != null && nodeId != null,
    },
  );
};

const useGetJobRunsByJobIds = ({
  jobIDs, nodeRunID, enabled = true,
}) => {
  const orgId = useSelector(state => state.session.user.primaryOrganization.organization.ID);

  return useQuery(
    [FLOW_QUERY_KEYS.JOB_RUNS_BY_JOB_IDS, jobIDs, nodeRunID],
    async () => {
      if (!jobIDs?.length || !nodeRunID) return { jobRuns: [] };

      // Build query parameters for multiple jobIDs
      const params = new URLSearchParams();
      jobIDs.forEach(jobID => params.append('jobIDs', jobID));
      params.append('sourceJobRunID', nodeRunID);

      // Call Picanmix API to fetch export job runs by job IDs and the clean room question run ID
      const response = await axiosInstance.get(
        flow(orgId).getExportJobRuns(params.toString()),
      );
      return response.data;
    },
    {
      enabled: enabled && jobIDs?.length > 0 && nodeRunID != null,
    },
  );
};

const useGetFlowNodeExportDetailsWithJobRuns = ({
  crId, flowId, nodeId, nodeRunID, enabled = true,
}) => {
  // First, get the export jobs
  const {
    data: exportJobsResponse = { jobs: [], count: 0 },
    isFetching: isFetchingJobs,
    error: jobsError,
    refetch: refetchJobs,
  } = useGetFlowNodeExportJobs({
    crId,
    flowId,
    nodeId,
    enabled,
  });

  const jobIDs = exportJobsResponse.jobs?.map(job => job.ID) || [];

  // If no export jobs, return early without calling the second API
  const hasJobs = enabled && jobIDs.length > 0;

  // Second, get the job runs based on the job IDs (only if there are jobs)
  const {
    data: jobRunsResponse = { jobRuns: [] },
    isFetching: isFetchingJobRuns,
    error: jobRunsError,
    refetch: refetchJobRuns,
  } = useGetJobRunsByJobIds({
    jobIDs,
    nodeRunID,
    enabled: hasJobs,
  });

  // Combine the data only if there are jobs
  const combinedData = React.useMemo(() => {
    if (!hasJobs) {
      return { jobs: [], count: 0 };
    }
    return mergeExportJobsWithJobRuns(exportJobsResponse, jobRunsResponse);
  }, [hasJobs, exportJobsResponse, jobRunsResponse]);

  const refetch = React.useCallback(async () => {
    if (hasJobs) {
      await Promise.all([refetchJobs(), refetchJobRuns()]);
    }
    else {
      // Only refetch export jobs when there are no job runs to fetch
      await refetchJobs();
    }
  }, [hasJobs, refetchJobs, refetchJobRuns]);

  const result = {
    data: combinedData,
    isLoading: isFetchingJobs || (hasJobs && isFetchingJobRuns),
    error: jobsError || jobRunsError,
    refetch,
  };

  return result;
};

const useUpdateFlowNodeExportJobStatus = () => {
  const dispatch = useDispatch();
  const orgId = useSelector(state => state.session.user.primaryOrganization.organization.ID);

  return useMutation(
    async ({
      cleanRoomId, flowId, nodeId, jobId, status,
    }) => {
      const response = await axiosInstance.patch(
        flow(orgId).updateFlowNodeExportJobStatus(cleanRoomId, flowId, nodeId, jobId),
        {
          organizationID: orgId,
          cleanRoomID: cleanRoomId,
          flowID: flowId,
          nodeID: nodeId,
          jobID: jobId,
          status,
        },
      );
      return response.data;
    },
    {
      onSuccess: (data, variables) => {
        const statusText = variables.status === 'ACTIVE' ? 'enabled' : 'disabled';
        dispatch(showAlert({
          message: `Export job ${statusText} successfully`,
          type: 'success',
        }));
      },
      onError: (error) => {
        const message = error.response?.data?.message || 'Failed to update export job status';
        dispatch(showAlert({
          message,
          type: 'error',
        }));
      },
    },
  );
};

export {
  useCreateFlowNodeExportJob,
  useGetFlowNodeExportJobs,
  useGetJobRunsByJobIds,
  useGetFlowNodeExportDetailsWithJobRuns,
  useUpdateFlowNodeExportJobStatus,
};
